import axios from "axios";
import { message, notification } from "ant-design-vue";
import { getToken } from "../tokenManager";

const CancelToken = axios.CancelToken;

const domainStr = window.httpRequestDomain || process.env.VUE_APP_SERVER;

axios.defaults.withCredentials = true;

// let fetchingCount = 0;

const TIMEOUT = 60000;

const service = axios.create({
  baseURL: domainStr,
  timeout: TIMEOUT,
});

service.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) config["headers"]["token"] = token;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

service.interceptors.response.use(
  (response) => {
    const { data: res, config } = response;
    if (config.fetchOptions.isFile) {
      return Promise.resolve(res);
    } else {
      if (res.sysCode === -1) {
        notification.error({
          title: "警告",
          description: "系统错误",
          duration: 10,
        });
        console.error(res.sysError);
      } else if (res.bizCode === 0) {
        message.error(res.bizRemind);
      } else if (res.bizCode === 10000 || res.bizCode === 10001) {
        message.info(res.bizRemind);
        window.sessionStorage.clear();
        window.location.href = `${window.location.origin}/login`;
      } else {
        return Promise.resolve(res);
      }
    }
  },
  (error) => {
    console.error(error);
    if (error.message === `timeout of ${TIMEOUT}ms exceeded`) {
      notification.error({
        title: "警告",
        description: "请求超时",
        duration: 10,
      });
    }
    return Promise.reject(error);
  }
);

/**
 * @class 请求构造类
 */
class RequestInstance {
  /**
   * @constructor
   * @param {object} config
   * @param {('get'|'post')} config.method 请求类型
   * @param {string} config.url 请求地址
   * @param {string} config.contentType 请求body类型,默认为application/json
   * @param {object} config.fetchOptions 请求其它配置
   * @param {boolean} config.fetchOptions.hideLoading 隐藏loading提示
   * @param {boolean} config.fetchOptions.showBizRemind 显示业务错误提示
   * @param {boolean} config.fetchOptions.isFile 是否为文件
   */
  constructor(config) {
    const axiosConfig = { ...config };
    delete axiosConfig["lock"];

    if (axiosConfig.contentType) {
      axiosConfig.headers = {
        "content-type": config.contentType,
      };

      delete axiosConfig.contentType;
    }

    this.axiosConfig = axiosConfig;

    /** property {string} url 请求地址 */
    this.url = config.url;

    /** property {string} fullUrl 完整请求地址 */
    this.fullUrl = `${domainStr}${config.url}`;

    /** property {boolean} isLock 请求是否锁定(完成一次请求后再释放) */
    this.isLock = false;

    /** property {*} cancelTokenSource */
    this.cancelTokenSource = null;
  }

  /**
   * @typedef {object} CustomConfig
   * @property {boolean} hideLoading 隐藏loading提示
   * @property {boolean} showBizRemind 显示业务错误提示
   * @property {boolean} lock 锁定请求
   * @property {boolean} cancelPreRequests 取消之前请求
   */

  /**
   * @typedef {objcet} ResponseBody
   * @property {objcet} data
   * @property {number} bizCode
   * @property {string} bizRemind
   */

  /**
   * @property {function} request 请求调用
   * @param {object} data 请求参数
   * @param {CustomConfig} customConfig 请求其它配置
   * @returns {Promise<ResponseBody, Error>}
   */
  request(data = {}, customConfig = {}) {
    // const LoadingBox = window.document.querySelector("#loading-box");

    return new Promise((resolve, reject) => {
      if (this.isLock) {
        return reject("请求锁定中");
        // console.log('请求锁定中')
      } else {
        const temp = Object.assign({}, this.axiosConfig);
        temp.fetchOptions = Object.assign({}, temp.fetchOptions, customConfig);

        if (this.axiosConfig.method === "get") temp.params = data;
        else temp.data = data;

        if (customConfig.lock) this.isLock = true;

        if (!temp.fetchOptions.hideLoading) {
          // LoadingBox.style.display = "flex";
          // fetchingCount += 1;
        }

        if (customConfig.cancelPreRequests) {
          if (this.cancelTokenSource) this.cancelTokenSource.cancel("请求中断");
          this.cancelTokenSource = CancelToken.source();
        } else {
          if (!this.cancelTokenSource)
            this.cancelTokenSource = CancelToken.source();
        }

        if (temp.fetchOptions.isFile) temp.responseType = "blob";

        temp.cancelToken = this.cancelTokenSource.token;
        service(temp)
          .then((res) => resolve(res))
          .catch((error) => reject(error))
          .finally(() => {
            this.isLock = false;
            if (!temp.fetchOptions.hideLoading) {
              // fetchingCount -= 1;
              // if (fetchingCount === 0) LoadingBox.style.display = "none";
            }
          });
      }
    });
  }

  /**
   * 取消已发起的请求
   */
  cancelRequest() {
    this.cancelTokenSource && this.cancelTokenSource.cancel("请求中断");
  }
}

export { domainStr, RequestInstance };
